import { closeModal } from '@/helpers/closeModal';

const DISMISS_VELOCITY_PX_PER_MS = 0.45;
const DISMISS_DISTANCE_MIN_PX = 96;
const DISMISS_DISTANCE_VIEWPORT_RATIO = 0.14;
const DRAG_LOCK_MIN_Y = 8;
const DRAG_LOCK_HORIZONTAL_TOLERANCE = 1.6;
const RESTORE_TRANSITION = 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 220ms ease';
const DISMISS_TRANSITION = 'transform 180ms ease-out, opacity 180ms ease-out';

const prefersReducedMotion = () => (
  typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const findScrollableAncestor = (target, boundary) => {
  let node = target;
  while (node && node !== boundary) {
    if (node instanceof HTMLElement) {
      const style = window.getComputedStyle(node);
      const canScroll = /(auto|scroll)/.test(`${style.overflowY}${style.overflow}`);
      if (canScroll && node.scrollHeight > node.clientHeight) return node;
    }
    node = node.parentNode;
  }
  return null;
};

export const bindStoryDragClose = () => {
  if (!window.storeeelItems?.isMobile || window.storeeelItems?.frame !== 'circle') return;

  const modal = document.querySelector('.storeeel-modal');
  const groupWrapper = modal?.querySelector?.('.storeeel-swiper-group-wrapper');
  const menuArea = modal?.querySelector?.('.storeeel-menu-area-icons');
  if (!modal || !groupWrapper || modal.dataset.storeeelStoryDragCloseBound === 'true') return;
  modal.dataset.storeeelStoryDragCloseBound = 'true';
  // ブラウザの縦パン判定で pointercancel され、下スワイプが即復元されるのを防ぐ
  modal.style.touchAction = 'none';
  groupWrapper.style.touchAction = 'none';

  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let lastY = 0;
  let lastTs = 0;
  let velocityY = 0;
  let dragging = false;
  let locked = false;
  let activeInputType = null;

  const applyDrag = (dragY) => {
    const safeY = Math.max(0, dragY);
    const viewportHeight = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0, 1);
    const ratio = Math.min(1, safeY / viewportHeight);
    groupWrapper.style.transition = 'none';
    if (menuArea) menuArea.style.transition = 'none';
    modal.style.transition = 'none';
    groupWrapper.style.transform = `translate3d(0, ${safeY}px, 0) scale(${1 - ratio * 0.04})`;
    if (menuArea) menuArea.style.transform = `translate3d(0, ${safeY}px, 0)`;
    modal.style.backgroundColor = `rgba(39, 39, 39, ${Math.max(0.25, 0.87 - ratio * 0.62)})`;
  };

  const restore = () => {
    groupWrapper.style.transition = prefersReducedMotion() ? 'none' : RESTORE_TRANSITION;
    if (menuArea) {
      menuArea.style.transition = prefersReducedMotion() ? 'none' : RESTORE_TRANSITION;
      menuArea.style.transform = 'translate3d(0, 0, 0)';
    }
    modal.style.transition = prefersReducedMotion() ? 'none' : 'background-color 220ms ease';
    groupWrapper.style.transform = 'translate3d(0, 0, 0) scale(1)';
    modal.style.backgroundColor = 'rgba(39, 39, 39, 0.87)';
    window.setTimeout(() => {
      groupWrapper.style.transition = '';
      if (menuArea) menuArea.style.transition = '';
      modal.style.transition = '';
    }, prefersReducedMotion() ? 0 : 230);
  };

  const dismiss = () => {
    const viewportHeight = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0, 1);
    groupWrapper.style.transition = prefersReducedMotion() ? 'none' : DISMISS_TRANSITION;
    if (menuArea) menuArea.style.transition = prefersReducedMotion() ? 'none' : DISMISS_TRANSITION;
    modal.style.transition = prefersReducedMotion() ? 'none' : 'background-color 180ms ease';
    groupWrapper.style.transform = `translate3d(0, ${viewportHeight}px, 0) scale(0.96)`;
    if (menuArea) menuArea.style.transform = `translate3d(0, ${viewportHeight}px, 0)`;
    modal.style.backgroundColor = 'rgba(39, 39, 39, 0)';
    window.setTimeout(() => closeModal(), prefersReducedMotion() ? 0 : 170);
  };

  const shouldIgnoreStartTarget = (target) => {
    if (!(target instanceof Element)) return;
    if (target.closest('.storeeel-menu-area-icons, [data-storeeel-cta-host]')) return true;

    const scrollable = findScrollableAncestor(target, modal);
    return Boolean(scrollable && scrollable.scrollTop > 0);
  };

  const startDrag = (event, point, inputType, id) => {
    if (activeInputType) return;
    if (shouldIgnoreStartTarget(event.target)) return;

    pointerId = id;
    activeInputType = inputType;
    startX = point.clientX;
    startY = point.clientY;
    lastY = point.clientY;
    lastTs = event.timeStamp;
    velocityY = 0;
    dragging = false;
    locked = false;
  };

  const moveDrag = (event, point) => {
    if (!activeInputType) return;

    const dx = point.clientX - startX;
    const dy = point.clientY - startY;
    const now = event.timeStamp;
    const dt = Math.max(1, now - lastTs);
    velocityY = (point.clientY - lastY) / dt;
    lastY = point.clientY;
    lastTs = now;

    if (!locked && dy > DRAG_LOCK_MIN_Y) {
      // 少し斜めの下スワイプも閉じ操作として扱う
      locked = Math.abs(dx) <= dy * DRAG_LOCK_HORIZONTAL_TOLERANCE;
    }
    if (!locked) return;

    dragging = true;
    if (event.cancelable) event.preventDefault();
    applyDrag(dy);
  };

  const finishDrag = (event, point, isDismissEvent) => {
    if (!activeInputType) return;

    const dy = Math.max(0, point.clientY - startY);
    const threshold = Math.max(
      DISMISS_DISTANCE_MIN_PX,
      (window.innerHeight || 0) * DISMISS_DISTANCE_VIEWPORT_RATIO
    );
    // cancel はブラウザ都合で指を離す前にも発生するため、閉じ判定は終了イベントのみに限定する。
    const shouldDismiss = isDismissEvent
      && dragging
      && (dy > threshold || velocityY > DISMISS_VELOCITY_PX_PER_MS);

    pointerId = null;
    activeInputType = null;
    dragging = false;
    locked = false;

    if (shouldDismiss) {
      dismiss();
    } else {
      restore();
    }
  };

  const getTouchById = (touches) => {
    if (!touches) return null;
    return Array.from(touches).find((touch) => touch.identifier === pointerId) || null;
  };

  modal.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    startDrag(event, event, 'pointer', event.pointerId);
    if (activeInputType !== 'pointer' || pointerId !== event.pointerId) return;
    modal.setPointerCapture?.(pointerId);
  });

  modal.addEventListener('pointermove', (event) => {
    if (activeInputType !== 'pointer') return;
    if (pointerId !== event.pointerId) return;
    moveDrag(event, event);
  }, { passive: false });

  const finishPointerDrag = (event) => {
    if (activeInputType !== 'pointer') return;
    if (pointerId !== event.pointerId) return;
    modal.releasePointerCapture?.(pointerId);
    finishDrag(event, event, event.type === 'pointerup');
  };

  modal.addEventListener('pointerup', finishPointerDrag);
  modal.addEventListener('pointercancel', finishPointerDrag);

  modal.addEventListener('touchstart', (event) => {
    if (event.touches?.length !== 1) return;
    const touch = event.touches[0];
    startDrag(event, touch, 'touch', touch.identifier);
  }, { capture: true, passive: true });

  modal.addEventListener('touchmove', (event) => {
    if (activeInputType !== 'touch') return;
    const touch = getTouchById(event.touches);
    if (!touch) return;
    moveDrag(event, touch);
  }, { capture: true, passive: false });

  modal.addEventListener('touchend', (event) => {
    if (activeInputType !== 'touch') return;
    const touch = getTouchById(event.changedTouches);
    if (!touch) return;
    finishDrag(event, touch, true);
  }, { capture: true, passive: true });

  modal.addEventListener('touchcancel', (event) => {
    if (activeInputType !== 'touch') return;
    const touch = getTouchById(event.changedTouches);
    if (!touch) return;
    finishDrag(event, touch, false);
  }, { capture: true, passive: true });
};
